/** Who is signed in, and the one way in (X). Split from the api.ts monolith 2026-08-03; the barrel re-exports. */
import { API_URL, CALLBACK_URL, call, num, patch, post } from "./core";
import { ownOriginAvatar } from "./knock";
import { hashConsent } from "./plan";
// --- /api/v1/me -------------------------------------------------------------

export type Me = {
  displayName: string;
  /**
   * WHEN THIS PERSON NAMED THEMSELVES (ruling R-0915-3). Null means the name
   * above is still the one X hands over and still follows it; a date means they
   * wrote it in Settings, and nothing copies over it again. The Account page
   * says which of the two is true, because "why did my name change" is only
   * answerable if the screen ever said where the name came from.
   */
  displayNameSetAt: string | null;
  /**
   * Their own picture, as a path on our origin — the same string the door plate
   * serves a stranger, and null for somebody with no cached one, which renders
   * as a monogram. It arrives on `/me` so the owner's own chrome does not have
   * to read their door plate to learn their own face.
   */
  avatar: string | null;
  xHandle: string | null;
  /**
   * Where Nob writes to THIS person — the Morning Nob, and anything else the
   * product has to put in a mailbox. Spelled the way the wire spells it, which
   * is the rule for every parser here: a second accepted name is how six fields
   * of drift once hid for a milestone.
   *
   * THERE IS NO SECOND ADDRESS (ruling R-0828-3). `email` used to sit beside
   * this one — the address a session was opened with — and it is deleted with
   * the lane that wrote it: X hands over no mailbox, so the only address this
   * product holds is the one the owner nominated and proved. `null` is
   * "nowhere to write", which the profile says out loud rather than rendering
   * as a quiet blank.
   */
  contact_email: string | null;
  /** Optional — present today, used only to withhold regulated chips (FR-1.4). */
  jurisdiction?: string | null;
  /**
   * Whether the next knock this person sends will wait for a check first
   * (ruling R-0906-3) — the server probes a knocker once per account, on
   * their first knock (R-0911-1), and the knock start awaits it.
   *
   * It is the WHOLE of what the client is told about that check: no cost, no
   * score, no signal. All it buys is a sentence while the start
   * request is in flight, so the seconds are understood rather than suffered.
   */
  sender_check_due: boolean;
  /**
   * Every feature flag this session carries (ruling R-0912-1 rule 2), evaluated
   * on the server and handed over whole. Read it through `lib/flags.ts`, never
   * directly: `useFlag` is where a surface states the default that applies when
   * a flag is absent, and absent is the ordinary case.
   */
  flags: Record<string, boolean | string>;
  /**
   * WHAT THIS PERSON OWES THE LEGAL TEXT (ruling R-0919-2), decided entirely on
   * the server. `pending` is what the blocking screen renders — the document,
   * the version and the rollout's own "what changed" prose — and `sentence` is
   * the consent line whose SHA-256 the accept has to carry. Nothing here is
   * computed in the browser: no version comparison, no date arithmetic, no
   * opinion about which change was the important one.
   */
  legal: {
    pending: { document: "terms" | "privacy"; version: string; summary: string }[];
    accepted: { terms: string | null; privacy: string | null };
    sentence: string;
  };
};

/** Tolerant of shape, exact about spelling — the rule every parser here follows. */
function legalOf(raw: unknown): Me["legal"] {
  const b = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const pendingRaw = Array.isArray(b["pending"]) ? b["pending"] : [];
  const accepted = (
    typeof b["accepted"] === "object" && b["accepted"] !== null ? b["accepted"] : {}
  ) as Record<string, unknown>;
  return {
    pending: pendingRaw.flatMap((item) => {
      const p = (typeof item === "object" && item !== null ? item : {}) as Record<string, unknown>;
      const document = p["document"];
      const version = p["version"];
      if (document !== "terms" && document !== "privacy") return [];
      if (typeof version !== "string" || !version) return [];
      return [
        {
          document,
          version,
          summary: typeof p["summary"] === "string" ? p["summary"] : "",
        },
      ];
    }),
    accepted: {
      terms: typeof accepted["terms"] === "string" ? accepted["terms"] : null,
      privacy: typeof accepted["privacy"] === "string" ? accepted["privacy"] : null,
    },
    // A sentence this side invented is one nobody could prove was shown, so an
    // absent one is empty and the poster refuses to offer an accept at all.
    sentence: typeof b["sentence"] === "string" ? b["sentence"] : "",
  };
}

/** Boolean or non-empty string only; anything else is dropped rather than coerced. */
function flagsOf(raw: unknown): Record<string, boolean | string> {
  if (typeof raw !== "object" || raw === null) return {};
  const out: Record<string, boolean | string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "boolean" || typeof value === "string") out[key] = value;
  }
  return out;
}

/** `null` means unauthenticated (401), not "failed" — failures throw. */
export async function fetchMe(): Promise<Me | null> {
  const res = await fetch(`${API_URL}/api/v1/me`, { credentials: "include" });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`me: ${res.status}`);
  const body = (await res.json()) as {
    user?: Partial<Me>;
    contact_email?: unknown;
    sender_check_due?: unknown;
    flags?: unknown;
    legal?: unknown;
  };
  const u = body.user;
  if (!u) return null;
  // The api states contact_email at the TOP level of /me, beside `user`, not
  // inside it — it is a fact about where Nob writes, not a profile field.
  const contactEmail =
    typeof body.contact_email === "string" && body.contact_email ? body.contact_email : null;
  return {
    displayName: u.displayName ?? "",
    displayNameSetAt: typeof u.displayNameSetAt === "string" ? u.displayNameSetAt : null,
    // Through the same gate as every other picture on the wire: allowlisted,
    // then based on the api's origin. Passed raw, the root-relative path asked
    // the APP for it once deployed, and the app answers with its index page.
    avatar: ownOriginAvatar(u.avatar),
    xHandle: u.xHandle ?? null,
    contact_email: contactEmail,
    jurisdiction: u.jurisdiction ?? null,
    // Beside `contact_email` at the top level, for the same reason: it is a
    // fact about what the next knock will do, not a profile field. Anything
    // but `true` is false — the waiting line is an explanation, and a payload
    // that did not say so is not a reason to show one.
    sender_check_due: body.sender_check_due === true,
    // Only the two shapes a flag can have survive the parse. A payload from an
    // api with the analytics port dark carries `{}`, which is the same thing as
    // no flags at all — every caller falls back to its own default.
    flags: flagsOf(body.flags),
    legal: legalOf(body.legal),
  };
}

/**
 * `POST /legal/accept` (ruling R-0919-2) — the one write a person with a
 * pending acceptance may make.
 *
 * The hash is of the sentence the server sent and this screen rendered, taken
 * with the SAME function the checkout consent uses: the server re-derives its
 * own and refuses a mismatch, which is what makes "these are the words they
 * were shown" provable rather than asserted.
 */
export type AcceptLegalResult =
  { ok: true } | { ok: false; code: "UNAUTHENTICATED" | "STALE" | "UNKNOWN" };

export async function acceptLegal(
  document: "terms" | "privacy",
  version: string,
  sentence: string,
): Promise<AcceptLegalResult> {
  const sentence_sha256 = await hashConsent(sentence);
  const r = await call(() => post("/api/v1/legal/accept", { document, version, sentence_sha256 }));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (r.res.ok) return { ok: true };
  if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
  // 409 is a screen that has drifted from the server — a stale version or a
  // sentence we do not recognise. Both mean "read /me again", which is what
  // the poster does with it.
  return { ok: false, code: r.res.status === 409 ? "STALE" : "UNKNOWN" };
}

/**
 * `PATCH /me` — the owner names themselves (ruling R-0915-3).
 *
 * The one field on this payload a person may write. The server screens the
 * string and states its own refusal; this carries that sentence verbatim rather
 * than inventing a friendlier one, because the rule belongs to the API and a
 * form that paraphrases a rule it does not own eventually paraphrases it wrong.
 */
export type DisplayNameResult =
  | { ok: true; displayName: string }
  | { ok: false; code: "UNAUTHENTICATED" | "REFUSED" | "UNKNOWN"; message: string | null };

export async function saveDisplayName(name: string): Promise<DisplayNameResult> {
  const r = await call(() => patch("/api/v1/me", { display_name: name }));
  if (!r) return { ok: false, code: "UNKNOWN", message: null };
  const body = (r.body ?? {}) as {
    displayName?: unknown;
    error?: { code?: string; details?: { issues?: unknown } };
  };
  if (!r.res.ok) {
    if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED", message: null };
    const issues = body.error?.details?.issues;
    const first =
      Array.isArray(issues) && issues[0] && typeof issues[0] === "object"
        ? (issues[0] as { message?: unknown }).message
        : undefined;
    return {
      ok: false,
      code: "REFUSED",
      message: typeof first === "string" && first.trim() ? first.trim() : null,
    };
  }
  return { ok: true, displayName: typeof body.displayName === "string" ? body.displayName : name };
}

// --- auth -------------------------------------------------------------------

/** Better Auth social sign-in (the generic-OAuth "x" provider; FakeX in dev). */
export async function startXSignIn(as?: string): Promise<string> {
  const res = await post("/api/auth/sign-in/social", {
    provider: "x",
    callbackURL: CALLBACK_URL,
    // A failed exchange must land on the APP, not on the api host's root
    // (which is a 404): Better Auth defaults the error redirect to its own
    // base URL. Same page as success; the `?error=` query is what differs.
    errorCallbackURL: CALLBACK_URL,
  });
  if (!res.ok) throw new Error(`x sign-in: ${res.status}`);
  const body = (await res.json()) as { url?: string };
  if (!body.url) throw new Error("x sign-in: no redirect url");
  // `as` is understood only by the local stand-in provider, which is the only
  // thing that offers a choice of identity. The real provider ignores it.
  return as ? `${body.url}&as=${encodeURIComponent(as)}` : body.url;
}

export type DevIdentity = {
  handle: string;
  display_name: string;
  /**
   * X Premium on this stand-in identity — which decides WHICH trial the money
   * step offers (docs/plans.md §8 #1), and therefore which of the eight
   * onboarding flows a tester is about to walk. Read from the server rather
   * than inferred from the handle: the rule belongs to the identity port.
   */
  premium: boolean;
  /** Whether `resetDevUser` will hard-delete this account. `test_*` only. */
  disposable: boolean;
};

/**
 * Whether the API is running the LOCAL STAND-IN for X rather than X itself.
 * Every dev-only affordance in the SPA hangs off this one question, and it is
 * asked of `/api/health`'s per-port map rather than of the aggregate: a stack can be
 * live on Stripe and still be faking identity.
 */
export async function xIdentityPortIsMock(): Promise<boolean> {
  try {
    const health = await fetch(`${API_URL}/api/health`);
    if (!health.ok) return false;
    const ports = ((await health.json()) as { ports?: Record<string, string> }).ports ?? {};
    return ports["xIdentity"] === "mock";
  } catch {
    return false;
  }
}

/**
 * The identities the local stand-in can sign in as, or none when a real
 * provider is configured — the picker exists only where choosing is possible.
 *
 * The list is the SEEDED profiles. It is not the limit: the stand-in mints any
 * well-formed handle on demand (`startXSignIn("dave")`), which is what the
 * free-text field beside this list is for.
 */
export async function fetchDevIdentities(): Promise<DevIdentity[]> {
  try {
    if (!(await xIdentityPortIsMock())) return [];
    const res = await fetch(`${API_URL}/api/v1/dev/fake-x/identities`);
    if (!res.ok) return [];
    const raw: unknown = await res.json();
    return Array.isArray(raw)
      ? raw.flatMap((i) => {
          const o = (i ?? {}) as Record<string, unknown>;
          return typeof o["handle"] === "string"
            ? [
                {
                  handle: o["handle"],
                  display_name: String(o["display_name"] ?? ""),
                  premium: o["premium"] === true,
                  disposable: o["disposable"] === true,
                },
              ]
            : [];
        })
      : [];
  } catch {
    return [];
  }
}

/**
 * The flow-testing kit (local stacks only) — see `apps/api/src/routes/devKit.ts`
 * and `docs/testing-flows.md`.
 *
 * Both calls answer with a plain outcome rather than throwing, for the same
 * reason the two functions above answer with an empty list: these are buttons on
 * a development affordance, and a stack without the endpoints should render
 * nothing rather than an error boundary.
 */
export type DevResetResult = { ok: true } | { ok: false; message: string };

/**
 * Everything about a disposable identity, deleted, so the next sign-in is a
 * first sign-in. The server refuses any handle that is not `test_*`; the
 * refusal is its sentence, verbatim, because the rule is the server's.
 */
export async function resetDevUser(handle: string): Promise<DevResetResult> {
  const r = await call(() => post("/api/v1/dev/reset-user", { handle }));
  if (!r) return { ok: false, message: `Nob is not answering at ${API_URL}.` };
  if (r.res.ok) return { ok: true };
  const message = (r.body as { error?: { message?: unknown } }).error?.message;
  return {
    ok: false,
    message: typeof message === "string" && message ? message : `Reset failed (${r.res.status}).`,
  };
}

export type DevSeedKnockResult =
  { ok: true; seeded: number } | { ok: false; code: string; message: string };

/**
 * A BATCH of strangers knock at the signed-in owner's door, through the real
 * pipeline (ruling R-0825-3). Five people of deliberately different quality, so
 * the console has something to sort; the server picks the cast and the count,
 * and this only reports how many landed.
 */
export async function seedDevKnock(): Promise<DevSeedKnockResult> {
  const r = await call(() => post("/api/v1/dev/seed-knock", {}));
  if (!r) return { ok: false, code: "UNREACHABLE", message: "Nob is not answering." };
  const body = r.body as { seeded?: unknown; error?: { code?: unknown; message?: unknown } };
  if (r.res.ok && typeof body.seeded === "number" && body.seeded > 0) {
    return { ok: true, seeded: body.seeded };
  }
  const code = typeof body.error?.code === "string" ? body.error.code : "UNKNOWN";
  const message = typeof body.error?.message === "string" ? body.error.message : code;
  return { ok: false, code, message };
}

/**
 * The six-digit code the onboarding Email step just posted (ruling R-0826-3).
 *
 * IT READS THE STAND-IN MAIL ADAPTER'S OUTBOX, server side, because the stored
 * code is a salted digest and must stay one. Against real mail there is no
 * outbox and the answer is `null`, which is the correct answer rather than a
 * leak — and the route does not exist at all against a real identity port.
 *
 * The step is REQUIRED and has no skip, so without this a tester walking the
 * eight flows would leave the browser for a terminal at step 2 of every one.
 */
export async function fetchDevEmailCode(email: string): Promise<string | null> {
  try {
    const res = await fetch(`${API_URL}/api/v1/dev/email-code?email=${encodeURIComponent(email)}`);
    if (!res.ok) return null;
    const code = ((await res.json()) as { code?: unknown }).code;
    return typeof code === "string" && /^\d{6}$/.test(code) ? code : null;
  } catch {
    return null;
  }
}

/**
 * A door on this local stack (M-dev). NOT a product surface: Nob has no
 * directory, and the real answer to "where is their door" is "they gave you the
 * address". This exists so a developer can reach the door they opened as
 * somebody else without going through psql.
 */
export type DevDoor = {
  handle: string;
  display_name: string;
  has_house_rules: boolean;
  scored_knocks: number;
};

/** Empty against a real identity port, where the route does not exist at all. */
export async function fetchDevDoors(): Promise<DevDoor[]> {
  try {
    if (!(await xIdentityPortIsMock())) return [];
    const res = await fetch(`${API_URL}/api/v1/dev/doors`);
    if (!res.ok) return [];
    const raw = ((await res.json()) as { doors?: unknown }).doors;
    if (!Array.isArray(raw)) return [];
    const doors: DevDoor[] = [];
    for (const item of raw) {
      if (!item || typeof item !== "object") continue;
      const d = item as Record<string, unknown>;
      const handle = typeof d["handle"] === "string" ? d["handle"] : "";
      if (!handle) continue;
      doors.push({
        handle,
        display_name:
          typeof d["display_name"] === "string" && d["display_name"].trim()
            ? d["display_name"]
            : `@${handle}`,
        has_house_rules: d["has_house_rules"] === true,
        scored_knocks: num(d["scored_knocks"]),
      });
    }
    return doors;
  } catch {
    return [];
  }
}

/**
 * End the session server-side. The caller owns the local half — the query
 * cache and the stored drafts are the signed-out person's, and they go
 * together (`clearLocalSession`) or the next sign-in inherits them.
 */
export async function signOut(): Promise<void> {
  const res = await post("/api/auth/sign-out", {});
  if (!res.ok) throw new Error(`sign out: ${res.status}`);
}
